/**
 * SAD-1004 — which Workflows areas of the app rail this viewer can reach.
 *
 * Each predicate is the gate the area's landing page actually sits behind, so
 * the rail never offers a link the page would refuse:
 *   - Pursuits (/capture): zou's ListCaptureProjects refuses the whole list
 *     unless the viewer may `list` capture_project on the tenant root
 *     (services/zou/pkg/connecthandlers/capture.go, authorizeGrowthResource →
 *     growthScopeDenialReason). This asks the same resolver the same question.
 *   - Opportunities and Landscapes: morgans resolves every opportunity,
 *     notice and landscape RPC as kind `opportunity` on the tenant root
 *     (services/morgans/pkg/connecthandlers/canonical_opportunity.go,
 *     resolveAtTenantRoot). Both pages need `list` there.
 *   - Library (/library): zou filters every Library read through library_item
 *     on the item's scope; the page itself asks `list` on the tenant root.
 *   - Delivery, Projects: no page guard and no gated landing call. The
 *     Delivery hub is static and its lists drop rows per program, and
 *     Projects lists the viewer's memberships. Every member reaches them.
 *
 * Pursuits, Opportunities, Landscapes and the Library are Growth: their policies require
 * area:growth on every verb, so these resolves answer "does this viewer carry
 * Growth" without restating which roles do.
 *
 * Fail-closed: while loading or on a failed resolve, the Growth areas are
 * unreachable.
 */ import { useMemo } from 'react';
import { useAuth } from '../../contexts/authContext';
import { tenantRootRef } from './useTenantAreas';
import { useView } from './useView';
const LIST_ACTION = 'list';
export function captureListRef(tenantId) {
    return tenantRootRef(tenantId, 'capture_project');
}
export function opportunityListRef(tenantId) {
    return tenantRootRef(tenantId, 'opportunity');
}
export function libraryListRef(tenantId) {
    return tenantRootRef(tenantId, 'library_item');
}
/** The server's tenant-root list gate: allowed, not no_view, `list` not blocked. */ export function listPermitted(view) {
    return view.viewMode !== 'no_view' && view.can(LIST_ACTION);
}
export function useWorkflowAreas() {
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const capture = useView(captureListRef(tenantId), {
        action: LIST_ACTION,
        enabled: !!tenantId
    });
    const opportunities = useView(opportunityListRef(tenantId), {
        action: LIST_ACTION,
        enabled: !!tenantId
    });
    const library = useView(libraryListRef(tenantId), {
        action: LIST_ACTION,
        enabled: !!tenantId
    });
    const deliveryArea = useView(tenantRootRef(tenantId), {
        action: 'area:execution',
        enabled: !!tenantId
    });
    const growthOnly = opportunities.policy?.decision === 'allow' && listPermitted(opportunities) && deliveryArea.policy?.decision === 'deny' && !deliveryArea.isLoading;
    const captureReachable = listPermitted(capture);
    const opportunitiesReachable = listPermitted(opportunities);
    const libraryReachable = listPermitted(library);
    const isLoading = capture.isLoading || opportunities.isLoading || library.isLoading || deliveryArea.isLoading;
    return useMemo(()=>({
            growthOnly,
            isLoading,
            reachable: {
                capture: captureReachable,
                delivery: true,
                projects: true,
                opportunities: opportunitiesReachable,
                library: libraryReachable
            }
        }), [
        isLoading,
        captureReachable,
        opportunitiesReachable,
        libraryReachable,
        growthOnly
    ]);
}
